<script setup lang="ts">
import { computed } from "vue";
import errorIconUrl from "@/assets/img/cross.png";
import downIconUrl from "@/assets/img/down.svg?url";
import validIconUrl from "@/assets/img/tick.png";
import { type DropdownOption } from "@/models";

const model = defineModel<string | number | null>();

const props = defineProps<{
    id: string;
    options: DropdownOption[];
    placeholder?: string;
    placeholderValue?: string | number;
    name?: string;
    tabindex?: number;
    disabled?: boolean;
    hasError?: boolean;
    validationClass?: string;
    fullWidth?: boolean;
    ariaLabel?: string;
}>();

const emit = defineEmits(["blur", "change"]);

function handleChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    emit("change", target.value);
}

const modelSet = computed(
    () =>
        model.value &&
        model.value !== "" &&
        model.value !== props.placeholderValue
);

const stateIcon = computed(() => {
    if (props.hasError) return { src: errorIconUrl, class: "error" };
    if (modelSet.value) return { src: validIconUrl, class: "valid" };
    return null;
});
</script>

<template>
    <div
        class="custom-select"
        :class="{
            'custom-select-error': props.hasError,
            'custom-select-valid': modelSet && !props.hasError,
            'custom-select-fullwidth': props.fullWidth ?? true,
        }"
    >
        <select
            :id="props.id"
            v-model="model"
            :name="props.name"
            :tabindex="props.tabindex ?? 0"
            :disabled="props.disabled"
            :aria-label="props.ariaLabel"
            class="custom-select-field"
            :class="[
                props.validationClass,
                {
                    'custom-select-field-value':
                        model !== props.placeholderValue,
                },
                {
                    'custom-select-field-placeholder':
                        model === props.placeholder,
                },
            ]"
            autocomplete="off"
            @blur="$emit('blur', $event)"
            @change="handleChange"
        >
            <option :value="props.placeholderValue ?? ''" disabled selected>{{
                props.placeholder || "Select"
            }}</option>
            <option
                v-for="option in props.options"
                :key="option.value"
                :value="option.value"
            >
                {{ option.label }}
            </option>
        </select>

        <img
            v-if="stateIcon"
            :src="stateIcon.src"
            :class="[
                'custom-select-icon',
                `custom-select-icon-${stateIcon.class}`,
            ]"
            :alt="stateIcon.class"
        />
        <img
            :src="downIconUrl"
            alt="Dropdown arrow"
            class="custom-select-icon custom-select-icon-arrow"
        />
    </div>
</template>

<style scoped>
.custom-select {
    position: relative;
    width: 100%;
    display: inline-block;
}

.custom-select-field {
    width: 100%;
    height: 46px;
    padding: 12px 35px 12px 15px;
    border: 1px solid #aaa;
    border-radius: 3px;
    background-color: #fff;
    font-size: var(--font-size-input);
    box-sizing: border-box;
    appearance: none;
}

.custom-select-field-value {
    color: #303438;
}

.custom-select-field-placeholder {
    color: var(--grey-taupe);
}

.custom-select:not(.custom-select-fullwidth) {
    width: calc(((100% - (3 - 1) * 0.75rem) / 3) + 0.75rem);
}

.custom-select-field:focus {
    outline: none;
    border-color: #4c4c4c;
    border-left-width: 3px;
    padding-left: 13px !important;
}

.custom-select::after {
    content: "";
    position: absolute;
    top: 6px;
    bottom: 6px;
    right: 45px;
    width: 1px;
    background-color: #ddd;
    pointer-events: none;
}

.custom-select-icon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
}

.custom-select-icon-arrow {
    right: 16px;
    width: 16px;
    height: 16px;
}

.custom-select-icon-error,
.custom-select-icon-valid {
    right: 52px;
    width: 28px;
    height: 28px;
}

.custom-select-error .custom-select-field {
    border-color: #e63946 !important;
    background-color: #fdeded !important;
}

.custom-select-error::after {
    background-color: #e63946;
}

.custom-select-valid .custom-select-field {
    border-color: #71cbf4 !important;
    background-color: #eef8fc !important;
}

.custom-select-valid::after {
    background-color: #0a5;
}

select {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
}

@media screen and (width <= 584px) {
    .custom-select {
        width: 100% !important;
    }
}
</style>
